import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';

const DB_NAME = 'pulse-approver-keys';
const STORE = 'keys';
export const SIGNING_ALG = 'ECDSA-P256-SHA256';

function bytesToB64(bytes) {
    let s = '';
    const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    for (let i = 0; i < arr.length; i += 1) s += String.fromCharCode(arr[i]);
    return btoa(s);
}

function b64ToBytes(b64) {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
}

export async function sha256Hex(bytes) {
    const buf = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function keyId(tenantId, uid) {
    return String(tenantId || '') + ':' + String(uid || '');
}

function openKeyDb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            const dbx = req.result;
            if (!dbx.objectStoreNames.contains(STORE)) dbx.createObjectStore(STORE);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error('Could not open signing-key store'));
    });
}

async function idbGet(id) {
    const dbx = await openKeyDb();
    return new Promise((resolve, reject) => {
        const tx = dbx.transaction(STORE, 'readonly');
        const q = tx.objectStore(STORE).get(id);
        q.onsuccess = () => resolve(q.result || null);
        q.onerror = () => reject(q.error);
    });
}

async function idbSet(id, value) {
    const dbx = await openKeyDb();
    return new Promise((resolve, reject) => {
        const tx = dbx.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(value, id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function fingerprintSpki(spkiB64) {
    const hex = await sha256Hex(b64ToBytes(spkiB64));
    return hex.slice(0, 32).replace(/(.{4})/g, '$1 ').trim();
}

export function canonicalApprovalPayload(input) {
    return JSON.stringify({
        v: 1,
        alg: SIGNING_ALG,
        uid: input.uid,
        email: input.email,
        name: input.name,
        role: input.role,
        tenantId: input.tenantId,
        policyId: input.policyId,
        policyNumber: input.policyNumber || '',
        coverage: input.coverage || '',
        holder: input.holder || '',
        note: input.note || '',
        signedAt: input.signedAt
    });
}

export async function ensureApproverKey({ tenantId, uid }) {
    if (!window.crypto || !window.crypto.subtle) {
        throw new Error('This browser cannot create a cryptographic signature.');
    }
    const id = keyId(tenantId, uid);
    let pair = await idbGet(id);
    if (pair && pair.privateKey && pair.publicKeySpki) return pair;

    const keys = await window.crypto.subtle.generateKey(
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['sign', 'verify']
    );
    const spki = await window.crypto.subtle.exportKey('spki', keys.publicKey);
    const publicKeySpki = bytesToB64(spki);
    pair = {
        privateKey: keys.privateKey,
        publicKey: keys.publicKey,
        publicKeySpki,
        createdAt: new Date().toISOString()
    };
    await idbSet(id, pair);
    try {
        await setDoc(doc(db, 'tenants', tenantId, 'users', uid), {
            signingPublicKey: publicKeySpki,
            signingAlg: SIGNING_ALG,
            signingKeyFingerprint: await fingerprintSpki(publicKeySpki),
            signingKeyUpdatedAt: pair.createdAt
        }, { merge: true });
    } catch (err) {
        console.warn('Public key stored on the signed policy; user profile update skipped.', err);
    }
    return pair;
}

export async function signPolicyApproval({ session, policy, note }) {
    const user = session && session.uid ? session : null;
    if (!user || !user.uid || !user.email) {
        throw new Error('You must be signed in as the approver to apply a digital signature.');
    }
    const signedAt = new Date().toISOString();
    const payload = canonicalApprovalPayload({
        uid: user.uid,
        email: user.email,
        name: user.displayName || user.email,
        role: user.role || 'manager',
        tenantId: user.tenantId,
        policyId: policy.id,
        policyNumber: policy.policyNumber || '',
        coverage: policy.productName || policy.coverageType || '',
        holder: (policy.policyHolder && policy.policyHolder.fullName) || '',
        note: String(note || '').trim(),
        signedAt
    });
    const encoded = new TextEncoder().encode(payload);
    const pair = await ensureApproverKey({ tenantId: user.tenantId, uid: user.uid });
    const sigBuf = await window.crypto.subtle.sign(
        { name: 'ECDSA', hash: 'SHA-256' },
        pair.privateKey,
        encoded
    );
    const signature = bytesToB64(sigBuf);
    const payloadHash = await sha256Hex(encoded);
    const ok = await verifyPolicySignature({
        payload,
        signature,
        publicKeySpki: pair.publicKeySpki
    });
    if (!ok) throw new Error('Signature self-check failed. Approval was not applied.');

    return {
        alg: SIGNING_ALG,
        signatureId: 'SIG-' + payloadHash.slice(0, 16).toUpperCase(),
        signerUid: user.uid,
        signerEmail: user.email,
        signerName: user.displayName || user.email,
        signerRole: user.role || 'manager',
        tenantId: user.tenantId,
        signedAt,
        payload,
        payloadHash,
        signature,
        publicKey: pair.publicKeySpki,
        fingerprint: await fingerprintSpki(pair.publicKeySpki)
    };
}

export async function verifyPolicySignature({ payload, signature, publicKeySpki }) {
    try {
        const key = await window.crypto.subtle.importKey(
            'spki',
            b64ToBytes(publicKeySpki),
            { name: 'ECDSA', namedCurve: 'P-256' },
            true,
            ['verify']
        );
        return window.crypto.subtle.verify(
            { name: 'ECDSA', hash: 'SHA-256' },
            key,
            b64ToBytes(signature),
            new TextEncoder().encode(payload)
        );
    } catch (err) {
        return false;
    }
}
